import { Button, ButtonSkeleton } from "@/components/ui/button";
import { CardSkeleton } from "@/components/ui/card";
import { toast } from "@/components/ui/toast";
import { Toolbar, ToolbarItem } from "@/components/ui/toolbar";
import { TextSkeleton } from "@/features/projects/components/text-skeleton";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/20/solid";
import { useEffect } from "react";

/* Prop Types */
interface ReviewMediaSkeletonProps {
  uploadLength: number;
  isError?: boolean;
}

/*
 * ReviewMediaSkeleton component
 * Skeleton component used for study-upload and study-review page
 */
export const ReviewMediaSkeleton = ({ uploadLength, isError }: ReviewMediaSkeletonProps) => {
  /* Side effects */
  useEffect(() => {
    // this is mainly used for the study-review page due to it not having a loading modal
    if (isError) {
      toast({ title: "An Error has occured", type: "error" });
    }
  }, [isError]);

  /* Render */
  return (
    <>
      <Toolbar>
        <ToolbarItem>
          <h1 className="font-semibold">Review assets</h1>
        </ToolbarItem>
        <ToolbarItem>
          <Button variant="primary" disabled>
            Choose context
          </Button>
        </ToolbarItem>
      </Toolbar>

      <div className="grow flex flex-row gap-4 max-h-full overflow-hidden">
        <div className="flex flex-col gap-4 overflow-y-auto min-w-[10vw]">
          {/* AssetThumbnails */}
          {Array.from({ length: uploadLength }).map((_, i) => (
            <CardSkeleton key={i} size="small" loadBody={false} />
          ))}
        </div>

        {/* Editor */}
        <div className="grow flex flex-row bg-base-300 rounded-lg overflow-hidden max-h-full relative skeleton">
          {/* Add region */}
          <div className="absolute top-0 left-0 p-4 z-20">
            <Button>
              <PlusIcon className="size-3.5 inline-block mr-1" /> <span className="text-sm font-semibold">AOI</span>
            </Button>
          </div>
          <div className="absolute right-4 top-4 z-20">
            <Button>
              100% <ChevronDownIcon className="size-3.5 inline-block" />
            </Button>
          </div>
        </div>

        {/* RegionList */}
        <div className="flex flex-col min-w-96 max-w-[25vw] p-4 pt-0 min-h-full rounded-lg">
          <h3 className="font-semibold pb-2">Areas of interest (AOI)</h3>
          <p className="opacity-70">We automatically detected areas of interest (AOIs) within the asset. You can edit AOIs here:</p>
          <div className="flex flex-col overflow-auto h-full pt-4">
            <div className="grid grid-cols-[76px_4fr_1fr] rounded-t-lg bg-base-200 p-3 items-center justify-between">
              <p className="text-sm opacity-60 pl-3">Type</p>
              <p className="text-sm opacity-60 pl-3">Label</p>
            </div>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="grid grid-cols-[76px_4fr_1fr] p-3 items-center justify-between">
                <div className="size-4 skeleton ml-3"></div>
                <TextSkeleton variant="p" className="ml-3" />
                <div className="justify-self-end size-4 skeleton ml-3"></div>
              </div>
            ))}
            <ButtonSkeleton />
          </div>
        </div>
      </div>
    </>
  );
};
